import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AzureAD } from 'react-aad-msal'
import { authProvider } from '../../../services/authProvider'
import store from '../../../store/configureStore'
import { Container, Row, Col, Badge, Button, Input } from 'reactstrap'
import PageContainer from '../../Shared/LayoutComponents/PageContainer'
import PageHeading from '../../Shared/LayoutComponents/PageHeading'
import LoadingSpinner from '../../Shared/LoadingSpinner'
import {
  fetchLicenseTenantDetail,
  clearLicenseTenantDetail,
  syncLicenseTenant,
  fetchLicenseSettings,
  stageCartLine,
  removeCartLine,
  clearCart,
} from '../../actions/cspLicensesActions'
import CSPLicenseCheckoutModal from './CSPLicenseCheckoutModal'
import CSPLicenseDevPanel from './CSPLicenseDevPanel'
import { useCSPLicenseEffectiveRoles } from './devImpersonation'
import { canUseLicenceFeature } from './featureAccess'
import {
  resolveHardCap, resolveThreshold, linesOverCap, linesOverThreshold as linesOverThresholdOf, canCheckout as canCheckoutFrom,
} from './cartLimits'
import { formatDate, formatDateTime } from '../../Shared/formatDateTime'

const TERM_LABELS = { P1M: 'Monthly', P1Y: 'Yearly', P2Y: '2-Yearly', P3Y: '3-Yearly' }
const formatTerm = (raw) => {
  if (!raw) return '—'
  return raw.split(',').map((s) => s.trim())
    .map((s) => TERM_LABELS[s.toUpperCase()] || s).join(', ')
}


const SkuIdentity = ({ row }) => (
  <>
    <strong>{row.skuDisplayName}</strong>
    {row.stockDescription && row.stockDescription !== row.skuDisplayName && (
      <div className="small">{row.stockDescription}</div>
    )}
    <div><code className="small text-muted">{row.stockCode}</code></div>
  </>
)

// Editable row when this is a single-sub SKU; the +/- controls feed into
// the cart. The threshold and hard-cap warnings stay inline so the
// engineer sees them at the moment they're staging an over-limit line.
const EditableSkuRow = ({ row, tenantId, threshold, hardCap, cartLine }) => {
  const dispatch = useDispatch()
  const [draft, setDraft] = useState(cartLine?.requestedQuantity ?? row.totalQuantity)

  // Re-sync when cart state changes externally (e.g. cart-clear).
  useEffect(() => {
    setDraft(cartLine?.requestedQuantity ?? row.totalQuantity)
  }, [cartLine, row.totalQuantity])

  const delta = draft - row.totalQuantity
  // Threshold gates absolute delta - large removals need approval
  // too, even though they're lower commercial risk. Mirror's the
  // server-side check in CSPLicenseRequestService.Pipeline.cs.
  const overThreshold = threshold !== null && Math.abs(delta) > threshold
  // Hard cap stays adds-only - catastrophic-add protection, not
  // a magnitude check. Removes are operationally recoverable.
  const overHardCap = hardCap !== null && delta > hardCap

  const stage = (newQty) => {
    if (newQty < 1) return
    setDraft(newQty)
    if (newQty === row.totalQuantity) {
      dispatch(removeCartLine(row.stockCode))
      return
    }
    dispatch(stageCartLine({
      tenantId,
      stockCode: row.stockCode,
      skuDisplayName: row.skuDisplayName,
      // Long-form SKU description carried through to the checkout DTO so
      // the server can persist it on the line item and downstream views
      // (approval modal, My Requests) can show the full name on hover.
      stockDescription: row.stockDescription,
      cspSubscriptionId: row.underlyingSubscriptionIds?.[0],
      previousQuantity: row.totalQuantity,
      requestedQuantity: newQty,
    }))
  }

  return (
    <tr className={cartLine ? 'table-active' : ''}>
      <td><SkuIdentity row={row} /></td>
      <td style={{ minWidth: 160 }}>
        <div className="d-flex align-items-center">
          <Button color="outline-secondary" size="sm" onClick={() => stage(draft - 1)}>−</Button>
          <Input
            type="number"
            min={1}
            value={draft}
            onChange={(e) => stage(parseInt(e.target.value, 10) || row.totalQuantity)}
            bsSize="sm"
            style={{ width: 70, margin: '0 6px', textAlign: 'center' }}
          />
          <Button color="outline-secondary" size="sm" onClick={() => stage(draft + 1)}>+</Button>
        </div>
        {delta !== 0 && (
          <div className="small text-muted mt-1">
            was {row.totalQuantity} ({delta > 0 ? `+${delta}` : delta})
          </div>
        )}
        {overHardCap && (
          <div className="small text-danger mt-1">
            You can add up to {hardCap} seats per subscription here. For larger orders, please contact Sales.
          </div>
        )}
        {!overHardCap && overThreshold && (
          <div className="alert alert-warning small py-1 px-2 mt-1 mb-0">
            This change requires approval.
          </div>
        )}
      </td>
      <td><Badge color="success">Active</Badge></td>
      <td>{formatTerm(row.termSummary)}</td>
      <td>{formatDate(row.nextRenewalDate)}</td>
    </tr>
  )
}

// Collapsed-by-default section listing globally-disabled SKUs that
// happen to exist on this tenant. Wording mirrors the multi-sub
// off-ramp's "contact Sales Admin to modify" so the user sees
// consistent language for any read-only off-ramp.
const DisabledProductsSection = ({ rows }) => {
  const [expanded, setExpanded] = useState(false)
  return (
    <Row className="mt-4">
      <Col>
        <div className="card">
          <div
            className="card-header d-flex align-items-center"
            style={{ cursor: 'pointer' }}
            onClick={() => setExpanded((v) => !v)}
          >
            <strong>Disabled products ({rows.length})</strong>
            <small className="text-muted ml-2">
              {expanded ? 'Click to collapse' : 'Click to expand'}
            </small>
          </div>
          {expanded && (
            <div className="card-body py-2">
              <div className="alert alert-warning small py-2 mb-2">
                These products need Sales Admin assistance. Raise a ticket
                on the Sales Admin queue to modify.
              </div>
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '60%' }}>SKU</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.stockCode}>
                      <td><SkuIdentity row={row} /></td>
                      <td>{row.totalQuantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Col>
    </Row>
  )
}

const ReadOnlySkuRow = ({ row }) => (
  <tr className="table-warning">
    <td><SkuIdentity row={row} /></td>
    <td>{row.totalQuantity}</td>
    <td>
      <Badge color="warning">{row.underlyingSubscriptionCount} subscriptions</Badge>
      <div className="small text-muted mt-1">
        Multiple subscriptions exist for this product. Contact Sales to update.
      </div>
    </td>
    <td>{formatTerm(row.termSummary)}</td>
    <td>{formatDate(row.nextRenewalDate)}</td>
  </tr>
)

const CartPanel = ({ tenantId, threshold, hardCap, onCheckout, stockCodeSets }) => {
  const dispatch = useDispatch()
  const cart = useSelector((s) => s.cspLicenses.cart)
  if (!cart.lines.length || cart.tenantId !== tenantId) return null

  // Classify each staged line. Both states block checkout - the submit cannot
  // succeed either way - but they are different problems and must say so.
  const lineState = (l) => {
    if (!stockCodeSets) return null
    if (stockCodeSets.editable.has(l.stockCode)) return null
    return stockCodeSets.known.has(l.stockCode) ? 'disabled' : 'gone'
  }

  // A cart line is a snapshot taken when it was staged. The detail page only
  // ever renders ACTIVE subscriptions, so a line whose stock code has since
  // dropped out of that set belongs to a subscription DD no longer considers
  // live (a sync between staging and checkout is enough). Submitting it fails
  // the pre-flight gate with SUB_REMOVED_AT_DD, and because the basket is
  // atomic that one dead line takes every healthy line down with it. Catch it
  // here, while the user can still act on it.
  const goneLines = cart.lines.filter((l) => lineState(l) === 'gone')
  const disabledLines = cart.lines.filter((l) => lineState(l) === 'disabled')
  const blockedCount = goneLines.length + disabledLines.length

  // Threshold uses absolute delta so a large removal also routes
  // to approval. Hard cap stays adds-only (catastrophic-add
  // protection); see EditableSkuRow comments above.
  const linesOverThreshold = linesOverThresholdOf(cart.lines, threshold)
  const linesOverHardCap = linesOverCap(cart.lines, hardCap)
  // Hard cap always blocks checkout (no override exists). Over-threshold
  // baskets are allowed to check out - they route to the approval queue
  // server-side and the checkout modal explains that to the user.
  // Stale lines block checkout for the same reason the hard cap does: the
  // submit cannot succeed, so offering the button only wastes an attempt and
  // writes a failed audit row.
  const canCheckout = canCheckoutFrom({
    hardCap, overCapCount: linesOverHardCap.length, blockedCount,
  })
  const needsApproval = linesOverThreshold.length > 0 && linesOverHardCap.length === 0

  return (
    <div className="card mb-3" style={{ position: 'sticky', top: 80 }}>
      <div className="card-header d-flex align-items-center">
        <strong>Staged changes ({cart.lines.length})</strong>
        <Button color="link" size="sm" className="ml-auto" onClick={() => dispatch(clearCart())}>
          Clear
        </Button>
      </div>
      <ul className="list-group list-group-flush">
        {cart.lines.map((l) => {
          const delta = l.requestedQuantity - l.previousQuantity
          const state = lineState(l)
          return (
            <li key={l.stockCode} className={`list-group-item small${state ? ' list-group-item-warning' : ''}`}>
              <div className="d-flex align-items-start">
                <div className="flex-grow-1">
                  <strong title={l.stockDescription && l.stockDescription !== l.skuDisplayName ? l.stockDescription : undefined}>
                    {l.skuDisplayName}
                  </strong>
                  <div className="text-muted">
                    {l.previousQuantity} → {l.requestedQuantity} ({delta > 0 ? `+${delta}` : delta})
                  </div>
                  {state === 'gone' && (
                    <div className="text-danger mt-1">
                      No longer available at Dicker Data — remove this line to continue.
                    </div>
                  )}
                  {state === 'disabled' && (
                    <div className="text-danger mt-1">
                      No longer manageable here — contact Sales Admin. Remove this line to continue.
                    </div>
                  )}
                </div>
                <Button
                  color="link"
                  size="sm"
                  className="text-danger p-0"
                  onClick={() => dispatch(removeCartLine(l.stockCode))}
                >
                  Remove
                </Button>
              </div>
            </li>
          )
        })}
      </ul>
      <div className="card-body">
        {goneLines.length > 0 && (
          <div className="alert alert-warning small py-2 mb-2">
            {goneLines.length === 1 ? '1 item is' : `${goneLines.length} items are`} no
            longer available at Dicker Data — the subscription was changed or cancelled
            after you staged the change. Remove{' '}
            {goneLines.length === 1 ? 'it' : 'them'} to submit the rest of this basket.
          </div>
        )}
        {disabledLines.length > 0 && (
          <div className="alert alert-warning small py-2 mb-2">
            {disabledLines.length === 1 ? '1 item' : `${disabledLines.length} items`} can no
            longer be managed here — the product was disabled after you staged the change.
            It is still active at Dicker Data; contact Sales Admin to change it. Remove{' '}
            {disabledLines.length === 1 ? 'it' : 'them'} to submit the rest of this basket.
          </div>
        )}
        {hardCap === null && (
          <div className="alert alert-warning small py-2 mb-2">
            Licence settings could not be loaded, so the per-subscription limit is unknown.
            Reload the page to try again — you can keep staging changes meanwhile.
          </div>
        )}
        {linesOverHardCap.length > 0 && (
          <div className="alert alert-danger small py-2 mb-2">
            You can add up to {hardCap} seats per subscription here. For larger orders, please contact Sales.
          </div>
        )}
        {needsApproval && (
          <div className="alert alert-info small py-2 mb-2">
            {linesOverThreshold.length} item(s) exceed your auto-approval
            limit of {threshold}. These changes require approval after you
            submit before they apply.
          </div>
        )}
        <Button color="primary" block disabled={!canCheckout} onClick={onCheckout}>
          {needsApproval ? 'Submit for approval' : 'Checkout'}
        </Button>
      </div>
    </div>
  )
}

const CSPLicenseTenantDetailInner = ({ tenantId, realRoles }) => {
  const dispatch = useDispatch()
  const detail = useSelector((s) => s.cspLicenses.tenantDetail)
  const loading = useSelector((s) => s.cspLicenses.tenantDetailLoading)
  const notFound = useSelector((s) => s.cspLicenses.tenantDetailNotFound)
  const settings = useSelector((s) => s.cspLicenses.settings)
  const cart = useSelector((s) => s.cspLicenses.cart)
  const syncingTenantId = useSelector((s) => s.cspLicenses.syncingTenantId)
  const isSyncing = syncingTenantId === tenantId

  const [checkoutOpen, setCheckoutOpen] = useState(false)

  // Stock codes the tenant can be edited against. The API returns only ACTIVE subs,
  // so this shrinks when a sync marks one REMOVED - which is how a staged cart line
  // goes stale. Two sets, because the reasons need different messages:
  //   editable - the SKU is still manageable here
  //   known    - the SKU is still ACTIVE at DD. Excluded products are disabled
  //              locally, not cancelled, so don't send the user to Dicker Data.
  // Both null while loading, so the cart doesn't flag every line.
  const stockCodeSets = useMemo(() => {
    if (!detail?.skus) return null
    const editable = new Set(detail.skus.map((r) => r.stockCode))
    const known = new Set(editable)
    for (const r of detail.hiddenSkus || []) known.add(r.stockCode)
    return { editable, known }
  }, [detail])

  // Threshold derived from the *effective* role set so the dev panel
  // can flip the cart's auto-approval limit while testing different
  // role configurations.
  const currentUserRoles = useCSPLicenseEffectiveRoles(realRoles)

  useEffect(() => {
    dispatch(fetchLicenseTenantDetail(tenantId))
    dispatch(fetchLicenseSettings())
    return () => {
      dispatch(clearLicenseTenantDetail())
      dispatch(clearCart())
    }
  }, [dispatch, tenantId])

  // Read fresh from settings rather than cached, so an admin edit shows on the
  // next page open. null when unknown - the cart then shows no approval warning
  // instead of guessing one; the server re-evaluates the threshold on submit.
  const threshold = useMemo(
    () => resolveThreshold(settings, currentUserRoles), [settings, currentUserRoles])

  // null when settings have not loaded or returned an unusable value. Checkout
  // is blocked while it is; staging still works, so a reload recovers.
  const hardCap = resolveHardCap(settings)

  if (loading && !detail) return <PageContainer><LoadingSpinner /></PageContainer>

  if (notFound) {
    return (
      <PageContainer>
        <Container fluid className="mt-3">
          <Row>
            <Col>
              <Link to="/CSPLicenses" className="small">&larr; Back to 365 Licenses</Link>
              <PageHeading>Tenant not found</PageHeading>
              <p>
                We couldn't load this tenant. It may have been unmapped or
                removed.
              </p>
            </Col>
          </Row>
        </Container>
      </PageContainer>
    )
  }

  if (!detail) return null

  return (
    <PageContainer>
      <Container fluid className="mt-3 mb-4">
        <Row className="align-items-start">
          <Col>
            <Link to="/CSPLicenses" className="small">&larr; Back to 365 Licenses</Link>
            <PageHeading>{detail.companyName || detail.tenantName}</PageHeading>
            <small className="text-muted">
              Tenant: <strong>{detail.tenantName}</strong>
              {detail.domainPrefix && (
                <> &middot; <code>{detail.domainPrefix}.onmicrosoft.com</code></>
              )}
              {detail.lastSyncedAt && (
                <> &middot; last synced {formatDateTime(detail.lastSyncedAt)}</>
              )}
            </small>
          </Col>
          <Col xs="auto" className="pt-3">
            <Button
              color="outline-primary"
              size="sm"
              disabled={isSyncing}
              onClick={() => dispatch(syncLicenseTenant(tenantId))}
            >
              {isSyncing ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-1" role="status" />
                  Syncing…
                </>
              ) : (
                'Sync from Dicker Data'
              )}
            </Button>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col md={cart.lines.length && cart.tenantId === tenantId ? 8 : 12}>
            {(detail.skus || []).length === 0 ? (
              <div className="text-muted">No active subscriptions for this tenant.</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '38%' }}>SKU</th>
                    <th style={{ width: '20%' }}>Quantity</th>
                    <th>Status</th>
                    <th>Term</th>
                    <th>Next renewal</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.skus.map((row) => {
                    if (row.hasMultipleSubscriptions) {
                      return <ReadOnlySkuRow key={row.stockCode} row={row} />
                    }
                    const cartLine = cart.tenantId === tenantId
                      ? cart.lines.find((l) => l.stockCode === row.stockCode)
                      : null
                    return (
                      <EditableSkuRow
                        key={row.stockCode}
                        row={row}
                        tenantId={tenantId}
                        threshold={threshold}
                        hardCap={hardCap}
                        cartLine={cartLine}
                      />
                    )
                  })}
                </tbody>
              </table>
            )}
          </Col>
          {cart.lines.length > 0 && cart.tenantId === tenantId && (
            <Col md={4}>
              <CartPanel
                tenantId={tenantId}
                threshold={threshold}
                hardCap={hardCap}
                onCheckout={() => setCheckoutOpen(true)}
                stockCodeSets={stockCodeSets}
              />
            </Col>
          )}
        </Row>

        {/* Disabled products section. Only renders when the
            tenant has SKUs that an admin has curated as off-limits via
            the Products page. Collapsed by default; banner + read-only
            rows mirror the multi-sub off-ramp's wording. The backend
            field is still hiddenSkus (DTO HiddenSkus) - UI terminology
            says "disabled" but the wire shape is unchanged. */}
        {(detail.hiddenSkus || []).length > 0 && (
          <DisabledProductsSection rows={detail.hiddenSkus} />
        )}
      </Container>

      <CSPLicenseCheckoutModal
        isOpen={checkoutOpen}
        toggle={() => setCheckoutOpen(false)}
        tenantId={tenantId}
        companyName={detail.companyName}
        threshold={threshold}
      />
      <CSPLicenseDevPanel />
    </PageContainer>
  )
}

const CSPLicenseTenantDetail = ({ match }) => (
  <AzureAD provider={authProvider} reduxStore={store}>
    {({ accountInfo }) => {
      const realRoles = accountInfo?.account?.idToken?.roles || []
      const allowed = canUseLicenceFeature(realRoles)

      if (!allowed) {
        return (
          <PageContainer>
            <Container fluid className="mt-3">
              <p>You do not have permission to view this page.</p>
            </Container>
          </PageContainer>
        )
      }

      return <CSPLicenseTenantDetailInner tenantId={match.params.tenantId} realRoles={realRoles} />
    }}
  </AzureAD>
)

export default CSPLicenseTenantDetail
